import {
  AutocompleteInputChangeReason,
  Grid,
  Typography,
  debounce,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import CustomAutocompleteMulti from "../../../../../common/components/functional/customAutocomplete/CustomAutocompleteMulti";
import { AutocompleteType } from "../../../../../common/types/autoComplete";
import { FormTitleGrid } from "../../../styles";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import { fetchSkills } from "../../../../skills/skillsSlice";
import { isFulfilled } from "@reduxjs/toolkit";
import getLocale from "../../../../../common/helpers/getLocale";

interface Options {
  skills: AutocompleteType[];
}

const initialSearchTerms = {
  skills: "",
};

export const Skills = () => {
  const dispatch = useAppDispatch();
  const locale = getLocale();
  const { t } = useTranslation("");
  const { control, watch } = useFormContext();

  const { orderTemplate, skills, isReadOnly } = watch();
  const localized = useAppSelector((state) => state.localized);
  const { skill: allSkillOptions } = localized;
  const skillOptions = Object.entries(allSkillOptions).map(([key, value]) => ({
    id: key,
    name: value,
  }));

  const [searchTerms, setSearchTerms] = useState(initialSearchTerms);
  const [fieldOptions, setFieldOptions] = useState<Options>({
    skills: skillOptions,
  });

  const onInputChange = debounce(
    (key: string, term: string, reason?: AutocompleteInputChangeReason) => {
      setSearchTerms({ ...searchTerms, [key]: term });

      if (!term) {
        setFieldOptions({ ...fieldOptions });
      }
    },
    400,
  );

  const sortedSkillOptions = useMemo(() => {
    return (
      fieldOptions.skills?.sort((a, b) => a.name?.localeCompare(b?.name)) || []
    );
  }, [fieldOptions.skills]);

  useEffect(() => {
    if (!orderTemplate?.workTitleId) return;
    dispatch(
      fetchSkills({
        language: locale.code,
        workTitleIds: orderTemplate.workTitleId,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        const sortedSkills = action.payload
          .map((skill) => ({
            ...skill,
            name: allSkillOptions[skill.id],
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
          .sort((a, b) => a.fieldOfWorkId.localeCompare(b.fieldOfWorkId));

        setFieldOptions({ skills: sortedSkills });
      }
    });
  }, [orderTemplate?.workTitleId, locale.code, dispatch, allSkillOptions]);

  return (
    <FormSection title={t("common:requiredSkills")}>
      <FormTitleGrid container item xs={4}>
        <Typography variant="secondary">{t("skills")}</Typography>
      </FormTitleGrid>
      <Grid item xs={8} display="flex">
        <Controller
          name="skills"
          control={control}
          render={({ field }) => (
            <CustomAutocompleteMulti
              name="skills"
              disabled={isReadOnly || !orderTemplate?.id}
              defaultValue={skills}
              options={sortedSkillOptions}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_: any, newValue: AutocompleteType[]) => {
                field.onChange(newValue);
              }}
              onInputChange={onInputChange}
              fullWidth
            />
          )}
        />
      </Grid>
    </FormSection>
  );
};
